import type { UserSubscription } from 'types';
import type { BillingAttentionReason } from 'utils/subscriptionState';

type RawSub = UserSubscription & Record<string, unknown>;

const str = (...vals: unknown[]): string | undefined => {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
};

const num = (...vals: unknown[]): number | undefined => {
  for (const v of vals) {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v)))
      return Number(v);
  }
  return undefined;
};

/** Card or wallet summary for display, e.g. "Visa ····4242", when API provides brand + last4. */
export function getPaymentMethodSummary(
  sub: UserSubscription | null | undefined,
): string | null {
  if (!sub) return null;
  const r = sub as RawSub;
  const stripeBrand =
    r.billingPaymentDetails &&
    typeof r.billingPaymentDetails === 'object' &&
    'card' in (r.billingPaymentDetails as Record<string, unknown>)
      ? (r.billingPaymentDetails as { card?: { brand?: string } }).card?.brand
      : undefined;
  const brand = str(r.paymentMethodBrand, r.payment_method_brand, stripeBrand);
  const stripeL4 =
    r.billingPaymentDetails &&
    typeof r.billingPaymentDetails === 'object' &&
    'card' in (r.billingPaymentDetails as Record<string, unknown>)
      ? (r.billingPaymentDetails as { card?: { last4?: string } }).card?.last4
      : undefined;
  const last4 = str(r.paymentMethodLast4, r.payment_method_last4, stripeL4);
  const brandNice = brand
    ? `${brand.charAt(0).toUpperCase()}${brand.slice(1).toLowerCase()}`
    : '';
  if (brandNice && last4) return `${brandNice} ····${last4}`;
  if (last4) return `····${last4}`;
  if (brandNice) return brandNice;
  return null;
}

/** Stripe Customer Portal or equivalent; optional env fallback for ops. */
export function getBillingPortalUrl(
  sub: UserSubscription | null | undefined,
): string | undefined {
  if (!sub) return undefined;
  const r = sub as RawSub;
  const u = str(r.billingPortalUrl, r.billing_portal_url);
  if (u) return u;
  const env = process.env.REACT_APP_BILLING_PORTAL_URL;
  return typeof env === 'string' && env.trim() ? env.trim() : undefined;
}

/** Hosted Stripe invoice page for the current overdue invoice. */
export function getOverdueInvoiceUrl(
  sub: UserSubscription | null | undefined,
): string | undefined {
  if (!sub) return undefined;
  const r = sub as RawSub;
  return str(r.overdueInvoiceUrl, r.overdue_invoice_url);
}

/** Primary CTA URL for billing-attention banners, keyed by attention reason. */
export function getBillingAttentionActionUrl(
  sub: UserSubscription | null | undefined,
  reason: BillingAttentionReason | null | undefined,
): string | undefined {
  if (!sub || !reason) return undefined;

  switch (reason) {
    case 'overdue_invoice':
      return getOverdueInvoiceUrl(sub) ?? getBillingPortalUrl(sub);
    case 'past_due':
    case 'unpaid':
      return getBillingPortalUrl(sub);
    default:
      return getBillingPortalUrl(sub);
  }
}

export function getCardExpiryParts(
  sub: UserSubscription | null | undefined,
): { month: number; year: number } | null {
  if (!sub) return null;
  const r = sub as RawSub;
  const stripeCard =
    r.billingPaymentDetails &&
    typeof r.billingPaymentDetails === 'object' &&
    'card' in (r.billingPaymentDetails as Record<string, unknown>)
      ? (
          r.billingPaymentDetails as {
            card?: { exp_month?: number; exp_year?: number };
          }
        ).card
      : undefined;
  const m = num(
    r.paymentMethodExpMonth,
    r.payment_method_exp_month,
    stripeCard?.exp_month,
  );
  const y = num(
    r.paymentMethodExpYear,
    r.payment_method_exp_year,
    stripeCard?.exp_year,
  );
  if (m != null && y != null && m >= 1 && m <= 12) return { month: m, year: y };
  return null;
}

export function getLastInvoicePdfUrl(
  sub: UserSubscription | null | undefined,
): string | undefined {
  if (!sub) return undefined;
  const r = sub as RawSub;
  return str(r.lastInvoicePdfUrl, r.last_invoice_pdf_url);
}

export function getLastInvoiceMeta(sub: UserSubscription | null | undefined): {
  amount: number;
  currency: string;
  paidAt: string;
} | null {
  if (!sub) return null;
  const r = sub as RawSub;
  const amount = num(r.lastInvoiceAmount, r.last_invoice_amount);
  const paidAt = str(r.lastInvoicePaidAt, r.last_invoice_paid_at);
  if (amount == null || !paidAt) return null;
  const currency = str(r.lastInvoiceCurrency, r.last_invoice_currency) || 'USD';
  return { amount, currency, paidAt };
}

export function getInvoiceContactEmail(
  sub: UserSubscription | null | undefined,
): string | undefined {
  if (!sub) return undefined;
  if (sub.enterpriseInvoiceRequest?.contactEmail) {
    return sub.enterpriseInvoiceRequest.contactEmail;
  }
  if (Array.isArray(sub.payerEmails) && sub.payerEmails[0]) {
    return sub.payerEmails[0];
  }
  return undefined;
}

/** Extra invoice recipients, excluding the signed-in user's default email. */
export function getInvoicePayerEmails(
  sub: UserSubscription | null | undefined,
  profileEmail?: string | null,
): string[] {
  if (!sub || !Array.isArray(sub.payerEmails)) return [];
  const defaultKey = profileEmail?.trim().toLowerCase();
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of sub.payerEmails) {
    if (typeof raw !== 'string') continue;
    const email = raw.trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key) || (defaultKey && key === defaultKey)) continue;
    seen.add(key);
    result.push(email);
  }
  return result;
}
